import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import AhrefsLandingHelmet from 'common/helmets/AhrefsLandingHelmet';
import Button from 'common/inputs/Button';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import UppercaseHeader from 'common/UppercaseHeader';
import Ahrefs1 from 'img/landing/ahrefs-1.webp';
import AhrefsWordmark from 'img/landing/ahrefs-wordmark.webp';
import Tim from 'img/landing/tim.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const AhrefsLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Ahrefs Case Study');
  }, [trackEvent]);

  return (
    <div className="landingAhrefs landingCustomersPage">
      <AhrefsLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <div className="text">
            <UppercaseHeader>Customer story</UppercaseHeader>
            <h1>
              How Ahrefs uses Canny to track feature requests and set customer&nbsp;expectations
            </h1>
            <Link className="cta" to="/register">
              <Button buttonType="greenButton" value="Get Started" />
            </Link>
          </div>
          <div className="headerImage">
            <div className="nametag">
              <div className="name">Tim Soulo</div>
              <div className="title">CMO at Ahrefs</div>
            </div>
            <LazyLoadedImage loading="eager" src={Tim} alt="Tim Soulo" />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              If you've looked into building up your company's SEO, you've probably come across
              Ahrefs. With customers from very successful companies like Netflix and Shopify, it's
              safe to say that Ahrefs is doing great&nbsp;work.
            </p>
            <p>
              Tim Soulo is CMO (Chief Marketing Officer) at Ahrefs but he wears many hats. Aside
              from marketing, he also does product management, works alongside designers, and does
              customer support. He likes to stay in touch with their customers and even says he
              believes support is a part of&nbsp;marketing.
            </p>
            <p className="quote">
              &ldquo;Many support requests are actually framed around
              suggesting&nbsp;features.&rdquo;
            </p>
            <p>
              So as the company grew and they acquired more customers, he felt the pain. They needed
              a better place to store and process their customer&nbsp;feedback.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>Tim described their feedback process before Canny with two words:</p>
            <p className="quote">&ldquo;Quite random&rdquo;</p>
            <p>
              Over the years, Ahrefs had customer feedback spread out over many different tools:
              Intercom, a Facebook group, Slack, Google Sheets, you name it. Needless to say, it was
              very&nbsp;unorganized.
            </p>
            <ul>
              <li>Get all feedback consolidated into one, organized&nbsp;place.</li>
              <li>
                Make sense of a large volume of feedback. Making it easy to understand
                and&nbsp;prioritize.
              </li>
              <li>Be able to follow up with customers to build on customer&nbsp;relationships.</li>
            </ul>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Tim had a vision for what he wanted in a feedback solution. After searching through
              several different options, he landed on&nbsp;Canny.
            </p>
            <p className="quote">
              &ldquo;The learning curve was almost nothing for me because Canny was so simple and
              intuitive. If I were to create customer feedback software, this is exactly how it
              would&nbsp;work.&rdquo;
            </p>
            <p>
              As an Ahrefs customer, you can easily find Canny in the help menu when you need it.
              When customers reach out from other channels like Facebook or Twitter, Tim and the
              team sends them a link to&nbsp;Canny.
            </p>
            <LazyLoadedImage alt="Ahrefs logo" className="border" src={Ahrefs1} />
            <p>
              The product team is the main user of Canny at Ahrefs. Tim oversees product development
              and uses Canny to help decide what they should work on. Designers use Canny as a
              reference and can ask questions to understand user&nbsp;goals.
            </p>
            <p>Of course, once they release a feature, they'll send updates through&nbsp;Canny.</p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              Since they started using Canny, over 1,000 Ahrefs customers have given feedback.
              Ahrefs is able to show their customers they&nbsp;care.
            </p>
            <p className="quote">
              &ldquo;I know if we stopped using Canny today, tracking feature requests would be
              a&nbsp;disaster.&rdquo;
            </p>
            <p>
              They also want their customers to understand that they have many things to work on. A
              customer can see that their request might be less popular than others. This sets their
              expectations that their request probably won't get prioritized right&nbsp;away.
            </p>
            <p>
              It's often difficult to avoid getting distracted by customer requests. At any given
              time, Tim has an area of focus. When a request is outside that area, he doesn't allow
              it to distract him. He appreciates being able to capture feedback in Canny so it's
              there when his focus&nbsp;changes.
            </p>
            <p className="quote">
              &ldquo;If you feel you're getting a lot of feedback and a big chunk of your time goes
              into discussing requests. If you have a hard time figuring out which features you
              should build next. If you want your product development to be community-driven. Canny
              is an awesome tool. I'm sure you'll love&nbsp;it.&rdquo;
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="Ahrefs Wordmark"
              className="ahrefsWordmark"
              src={AhrefsWordmark}
            />
            Ahrefs is a SEO toolset powered by big&nbsp;data.
            <a href="https://www.ahrefs.com/" rel="noopener" target="_blank">
              ahrefs.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>55</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2010</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Years on Canny</UppercaseHeader>
              <p>3</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>8,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};
export default AhrefsLanding;
